import * as React from 'react';
import { useStore } from 'react-redux';
import {
    setAiMarkingUnavailable,
    setAiQuestionMarked,
    subscribeAiMarkingReload,
} from 'common/features/footer/unsavedMarkingsGuard';
import { tl } from 'common/services/i18nService';
import apiService from '../../../services/api/apiService';
import { isAiMarkingReadOnly } from '../../../services/permissions/PermissionService';
import { ContextState } from '../../../store/context/reducer';
import { RootState } from '../../../store/rootReducer';
import {
    getShowAiSupportedMarking,
    subscribeShowAiSupportedMarking,
} from '../../aiSupportedMarkingIntro/showAiSupportedMarking';
import * as styles from './AiMarkingEmbedWrapper.scss';

// Rendered only once `shouldEmbedAiMarking` has said so, so the ids below are known good
// and there is no fallback to the built-in viewer here - see Submission.tsx.
interface Props {
    context: ContextState;
    // Optional safety flag to suppress network call (used only in tests/dev); not used by the app flow
    disabled?: boolean;
    // Candidate report view: always read-only, whatever the viewer's grading rights.
    forceReadOnly?: boolean;
    // Tells the applet the viewer is a candidate (candidate report view).
    isCandidate?: boolean;
}

const INSPERA_PROFILE_HASH = 'profile=inspera';

// Handshake with the Graide applet: it posts `graide:ready` once loaded, and we answer with
// `graide:auth` carrying the session token. The applet only accepts our answer if it comes from
// an allow-listed parent origin, and gives up after 30s.
const GRAIDE_READY = 'graide:ready';
const GRAIDE_AUTH = 'graide:auth';
const GRAIDE_SHOW_AI_MARKS = 'graide:showAiMarks';

// The applet sends this when it unsets its own isMarked state, so the stale score already pushed
// to the core grading system can be reset the same way the manual "Reset" button does.
const GRAIDE_IS_MARKED = 'graide:isMarked';

/** Where to load the applet, and how to authenticate it. */
interface EmbedSession {
    url: string;
    origin: string;
    token: string;
    clientId: string;
}

const buildAppletUrl = (url: string, readOnly: boolean, isCandidate: boolean, showAiMarks: boolean): string => {
    const appletUrl = new URL(url);
    appletUrl.searchParams.set('readonly', String(readOnly));
    if (isCandidate) {
        appletUrl.searchParams.set('isCandidate', 'true');
    }
    appletUrl.searchParams.set('showAiMarks', String(showAiMarks));
    appletUrl.hash = INSPERA_PROFILE_HASH;
    return appletUrl.toString();
};

const postShowAiMarks = (appletWindow: Window, origin: string) => {
    appletWindow.postMessage(
        { type: GRAIDE_SHOW_AI_MARKS, show: getShowAiSupportedMarking() },
        origin
    );
};

const getErrorTitle = () => tl('grading.aiMarking.embed.errorTitle', {},
    'Something went wrong while loading this section.');
const getErrorSubtitle = () => tl('grading.aiMarking.embed.errorSubtitle', {},
    'If this keeps happening, please send a service ticket for resolution.');
const getStandardErrorMessage = (): string => `${getErrorTitle()} ${getErrorSubtitle()}`;

const getDelayedErrorTitle = () => tl('grading.aiMarking.delayed.title', {}, 'Submission processing delayed');
const getDelayedErrorSubtitle = () => tl(
    'grading.aiMarking.delayed.subtitle',
    {},
    'Processing is delayed, and the submission is not ready yet. Please check again later.'
);

const AI_MARKING_LEARN_MORE_URL = 'https://support.inspera.com/hc/en-us/sections/39477667748637-AI-Supported-Marking';

const AiMarkingEmbedWrapper: React.FC<Props> = ({
    context, disabled = false, forceReadOnly = false, isCandidate = false,
}) => {
    const { questionId, assessmentRunId, partyId } = context;
    const readOnly = forceReadOnly || isAiMarkingReadOnly(context.partyState.gradingPartyDocument);

    const [session, setSession] = React.useState<EmbedSession | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [errorKind, setErrorKind] = React.useState<'standard' | 'delayed' | null>(null); // classification for error UI
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [reloadTick, setReloadTick] = React.useState<number>(0);
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const resetAbortRef = React.useRef<AbortController | null>(null);
    // Held in a ref as well as state: `graide:ready` can arrive before React re-renders, so the
    // listener needs a way to read the current session synchronously.
    const sessionRef = React.useRef<EmbedSession | null>(null);
    // Store access, not useSelector: avoids re-running this effect on every assessment-state change.
    const store = useStore<RootState>();

    React.useEffect(() => {
        const resetController = new AbortController();
        resetAbortRef.current = resetController;
        return () => {
            resetController.abort();
        };
    }, [questionId, assessmentRunId, partyId, readOnly, isCandidate]);

    React.useEffect(() => {
        // The embed url is the same for every question - the question, candidate and grader are
        // all carried in the token, not the url. So changing question leaves `src` byte-identical
        // and React never reloads the iframe, stranding the grader on the previous question's
        // marking. Dropping the session unmounts the iframe, so the new one loads from scratch
        // and redoes the handshake with the new token.
        setSession(null);
        sessionRef.current = null;
        setError(null);
        setErrorKind(null);
        setIsLoading(true);
        setAiQuestionMarked(false);

        const controller = new AbortController();

        if (disabled) {
            return () => {
                controller.abort();
                sessionRef.current = null;
            };
        }

        const onMessage = (event: MessageEvent) => {
            const currentSession = sessionRef.current;
            const appletWindow = iframeRef.current?.contentWindow;
            if (!currentSession || !appletWindow) {
                return;
            }
            if (event.origin !== currentSession.origin || event.source !== appletWindow) {
                return;
            }

            const data = event.data as { type?: string; isMarked?: boolean };

            if (data?.type === GRAIDE_READY) {
                appletWindow.postMessage(
                    { type: GRAIDE_AUTH, clientId: currentSession.clientId, token: currentSession.token },
                    currentSession.origin
                );
                postShowAiMarks(appletWindow, currentSession.origin);
                return;
            }

            if (data?.type === GRAIDE_IS_MARKED) {
                setAiQuestionMarked(data.isMarked === true);
                if (data.isMarked !== false || readOnly) {
                    return;
                }
                const { useGradingThresholds, numberOfNonStimulusQuestions } = store.getState().assessment;
                const resetSignal = resetAbortRef.current?.signal;
                const resetStaleScore = async () => {
                    if (!useGradingThresholds && numberOfNonStimulusQuestions === 1) {
                        await apiService.resetGrade(assessmentRunId, partyId, false, resetSignal);
                    }
                    await apiService.resetScore(assessmentRunId, partyId, questionId, resetSignal);
                };
                resetStaleScore().catch((e) => console.error('Failed to reset stale score', e));
            }
        };

        // Attached before the fetch resolves, so it is in place before the iframe exists: the
        // applet posts `graide:ready` from module scope, as soon as its bundle runs.
        window.addEventListener('message', onMessage);

        const fetchEmbed = async () => {
            try {
                const json = await apiService.getGraideMarkingSession(
                    questionId, assessmentRunId, partyId, controller.signal
                );
                if (controller.signal.aborted) {
                    return;
                }
                if (!json?.url) {
                    setError(getStandardErrorMessage());
                    setIsLoading(false);
                    return;
                }
                const embedSession: EmbedSession = {
                    url: buildAppletUrl(json.url, readOnly, isCandidate, getShowAiSupportedMarking()),
                    origin: new URL(json.url).origin,
                    token: json.token,
                    clientId: json.clientId,
                };
                sessionRef.current = embedSession;
                setSession(embedSession);
                setError(null);
            } catch (e) {
                if (!controller.signal.aborted) {
                    const status = e?.response?.status as number | undefined;
                    if (status === 500) {
                        let isDelayed = false;
                        try {
                            const body = typeof e?.response?.json === 'function' ? await e.response.json() : undefined;
                            const exceptionType: string | undefined = body?.ExceptionClassName;
                            // Only specific backend messages should be treated as delayed
                            if (exceptionType.includes('NotFoundException')) {
                                isDelayed = true;
                            }
                        } catch (_parseErr) {
                            // Ignore JSON parse errors and fall back to standard error
                        }
                        if (isDelayed) {
                            setErrorKind('delayed');
                            setError(getDelayedErrorTitle());
                        } else {
                            setErrorKind('standard');
                            setError(getStandardErrorMessage());
                        }
                    } else {
                        // 403 and other generic error
                        setErrorKind('standard');
                        setError(getStandardErrorMessage());
                    }
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        // No fallback by design: once the applet is framed it surfaces its own errors. The
        // rejection still has to be handled here so it does not surface as an unhandled rejection.
        // fetchEmbed().catch((e) => console.error('AI-supported marking embed failed', e));
        fetchEmbed();

        return () => {
            controller.abort();
            window.removeEventListener('message', onMessage);
            sessionRef.current = null;
        };
    }, [questionId, assessmentRunId, partyId, readOnly, isCandidate, reloadTick]);

    // While the error banner shows there is no marking to confirm or lose: the footer disables
    // Done & Next and skips the unsaved-markings prompt. Layout so the flag is set before paint.
    const showsErrorBanner = Boolean(error) && !isLoading;
    React.useLayoutEffect(() => {
        setAiMarkingUnavailable(showsErrorBanner);
    }, [showsErrorBanner]);
    React.useEffect(() => () => setAiMarkingUnavailable(false), []);

    // Done on the last hit has nowhere to navigate, so it asks for a fresh session instead.
    React.useEffect(() => subscribeAiMarkingReload(() => setReloadTick((x) => x + 1)), []);

    React.useEffect(() => subscribeShowAiSupportedMarking(() => {
        setReloadTick((tick) => tick + 1);
    }), []);

    if (session) {
        return (
            <div className={styles.playerPreview}>
                <iframe
                    ref={iframeRef}
                    src={session.url}
                    allowFullScreen
                    allow="fullscreen"
                    title={tl('grading.aiMarking.iframe.title')}
                />
            </div>
        );
    }

    if (error && !isLoading) {
        const handleRetry = () => {
            setSession(null);
            setError(null);
            setErrorKind(null);
            setIsLoading(true);
            setReloadTick((x) => x + 1);
        };
        if (errorKind === 'delayed') {
            return (
                <div className={styles.errorBanner} role="alert" aria-live="assertive">
                    <div className={styles.errorContent}>
                        <div className={styles.errorIcon} aria-hidden>!</div>
                        <div className={styles.errorText}>
                            <div className={styles.errorTitle}>{getDelayedErrorTitle()}</div>
                            <div className={styles.errorSubtitle}>{getDelayedErrorSubtitle()}</div>
                        </div>
                        <div className={styles.errorActions}>
                            <a
                                href={AI_MARKING_LEARN_MORE_URL}
                                className={styles.learnMoreLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {tl('grading.aiMarking.delayed.learnMore', {}, 'Learn more')}
                            </a>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.errorBanner} role="alert" aria-live="assertive">
                <div className={styles.errorContent}>
                    <div className={styles.errorIcon} aria-hidden>
                        {/* icon is decorative; using simple unicode fallback */}
                        !
                    </div>
                    <div className={styles.errorText}>
                        <div className={styles.errorTitle}>{getErrorTitle()}</div>
                        <div className={styles.errorSubtitle}>{getErrorSubtitle()}</div>
                    </div>
                    <div className={styles.errorActions}>
                        <button type="button" className={styles.tryAgainButton} onClick={handleRetry}>
                            {tl('grading.aiMarking.embed.tryAgain', {}, 'Try again')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.loading}>
            <span>{tl('grading.aiMarking.loading')}</span>
        </div>
    );
};

export default AiMarkingEmbedWrapper;
